import React, {
  useRef,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import * as turf from '@turf/turf';
import { toast } from 'sonner';
import useDisableBackSwipe from '@/hooks/useDisableBackSwipe';
import { BurstStatus, Frame, ScoutLocation } from '@/types/location';
import MiniMap from '@/components/ui/scout/map/MiniMap';
import Modal from '@/components/ui/scout/modal/Modal';
import Frames from '@/components/ui/scout/imagery/Frames';
import Imagery from '@/components/ui/scout/imagery/Imagery';
import { useScout } from '@/hooks/useScout';
import { getLatestBurst } from '@/utils/scout';
import {
  EditableText,
  Variant as EditableTextVariant,
} from '@/components/ui/common/EdittableText';
import { updateLocation } from '@/api/location';
import { Default as DefaultToast } from '@/components/ui/toast/Toast';
import { Views } from '@/types/view';
import Info from '../header/Info';
import Compass from '../imagery/Compass';

export interface DetailedProps {
  location: ScoutLocation;
  activeView: Views;
  setBurstModalOptions: Dispatch<
    SetStateAction<{
      isOpen: boolean;
      locationsIds: string[];
      successCallback: () => void;
    }>
  >;
}

const Detailed: React.FC<DetailedProps> = ({
  location,
  activeView,
  setBurstModalOptions,
}) => {
  const [sortedSequences, setSortedSequences] = useState<Frame[][]>([]);
  const [apiCallsComplete, setApiCallsComplete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [requestCost, setRequestCost] = useState<number>(0);
  const { setLocations } = useScout();

  const nullStateRef = useRef<HTMLDivElement>(null);

  const {
    activeSequenceIndex,
    setActiveSequenceIndex,
    activeFrameIndex,
    setActiveFrameIndex,
  } = useScout();

  useEffect(() => {
    if (location._id) {
      setApiCallsComplete(false);
      setSortedSequences([]);
    }
  }, [location._id, setSortedSequences]);

  useDisableBackSwipe();

  const onEditNameSubmit = useCallback(
    async (newValue: string) => {
      const response = await updateLocation(location._id, {
        name: newValue,
      });
      if (response.error) {
        toast.custom(t => (
          <DefaultToast
            title="Failed to update Scout location"
            description={(response.error as Error).message}
            buttons={[
              {
                label: 'Dismiss',
                onClick: () => toast.dismiss(t),
                variant: 'secondary',
                size: 'fw',
              },
            ]}
          />
        ));
        throw response.error;
      }

      setLocations(prev => {
        const newData = [...prev];
        const index = newData.findIndex(loc => loc._id === location._id);
        newData[index].name = newValue;
        return newData;
      });
    },
    [location._id, setLocations],
  );

  if (!location) {
    return (
      <div
        ref={nullStateRef}
        className="flex h-[calc(100dvh-215px)] w-full items-center justify-center">
        Press Upload to start monitoring locations with Scout.
      </div>
    );
  }

  const activeSequence = sortedSequences?.[activeSequenceIndex] || null;
  const centroid = turf.centroid(location.geojson as turf.AllGeoJSON);

  const latest = getLatestBurst(location.bursts);
  const hasActiveBurst = latest?.status === BurstStatus.ACTIVE;

  const bearing =
    activeSequence && activeSequence[activeFrameIndex.value]?.bearing;

  return (
    <>
      {sortedSequences && activeSequence && (
        <Modal showModal={showModal} setShowModal={setShowModal} fullScreen>
          {!!bearing && (
            <div className="absolute right-12 top-[18px] z-10">
              <Compass bearing={bearing} />
            </div>
          )}
          <Frames
            sortedSequences={sortedSequences}
            activeSequence={activeSequence}
            activeSequenceIndex={activeSequenceIndex}
            setActiveSequenceIndex={setActiveSequenceIndex}
            activeFrameIndex={activeFrameIndex}
            setActiveFrameIndex={setActiveFrameIndex}
            setShowModal={setShowModal}
          />
        </Modal>
      )}
      <div className="flex w-full flex-col gap-4">
        <div className="flex w-full">
          <div className="relative flex w-full flex-col gap-2 md:w-1/2">
            <div className="flex md:hidden [&>div]:justify-between">
              <Info
                requestCost={requestCost}
                latest={latest}
                hasActiveBurst={hasActiveBurst}
                location={location}
                setBurstModalOptions={setBurstModalOptions}
              />
            </div>
            <EditableText
              initialValue={location.name}
              variant={EditableTextVariant.Default}
              onSave={onEditNameSubmit}
              inputClassName="text-lg"
              textClassName="text-lg font-bold pl-0"
            />
            {location.description && (
              <div className="text-xs">{location.description}</div>
            )}
            {centroid && (
              <div className="text-xs">
                {`${centroid.geometry.coordinates[1]}, ${centroid.geometry.coordinates[0]}`}
              </div>
            )}
          </div>
          <div className="relative hidden w-1/2 md:flex [&>div]:justify-end">
            <Info
              requestCost={requestCost}
              latest={latest}
              hasActiveBurst={hasActiveBurst}
              location={location}
              setBurstModalOptions={setBurstModalOptions}
            />
          </div>
        </div>
        <div className="flex w-full flex-col">
          <div className="flex w-full flex-col md:flex-row">
            <div className="relative h-[25vh] w-full pb-3 md:min-h-[60vh] md:w-1/2 md:pb-0 md:pr-3">
              <MiniMap
                center={centroid.geometry.coordinates}
                geojson={location.geojson}
                sortedSequences={sortedSequences}
                activeSequence={activeSequence}
                activeSequenceIndex={activeSequenceIndex}
                apiCallsComplete={apiCallsComplete}
                activeFrameIndex={activeFrameIndex}
                setActiveSequenceIndex={setActiveSequenceIndex}
                setActiveFrameIndex={setActiveFrameIndex}
                hasActiveBurst={hasActiveBurst}
              />
            </div>
            <div className="flex size-full flex-wrap md:min-h-[60vh] md:w-1/2">
              <Imagery
                key={location._id}
                location={location}
                activeView={activeView}
                sortedSequences={sortedSequences}
                setSortedSequences={setSortedSequences}
                activeSequence={activeSequence}
                activeSequenceIndex={activeSequenceIndex}
                setActiveSequenceIndex={setActiveSequenceIndex}
                apiCallsComplete={apiCallsComplete}
                setApiCallsComplete={setApiCallsComplete}
                activeFrameIndex={activeFrameIndex}
                setActiveFrameIndex={setActiveFrameIndex}
                setShowModal={setShowModal}
                setRequestCost={setRequestCost}
                setBurstModalOptions={setBurstModalOptions}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Detailed;
